import React, { FC, ReactElement } from 'react';
import { GENERAL_DETAILS_ROOT } from 'src/domains/casino/utils/test-variables';
import { Input } from 'src/domains/common/components/input/input';
import { Select } from 'src/domains/common/components/select/select';
import { observer } from 'src/utils/mobx-react';
import { Calendar } from 'src/domains/casino/webview/bonus-engine/campaign/Calendar';
import { useCommon } from 'src/AppState/Common';
import { CampaignCreatorState } from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.state';
import { UK_TIMEZONE } from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.helpers';
import { StartAtTypeType } from 'src/api_openapi/v2/bonus-engine/schemas';
import { useAppStateContext } from 'src/AppState/AppState';
import { CheckboxWithState } from 'src/domains/common/components/checkbox-with-state/checkbox-with-state';
import { Textarea } from 'src/domains/layouts/shared/formComponents/textarea/Textarea';

type GeneralDetailsPropsType = {
    campaignId?: string;
};

export const GeneralDetails: FC<GeneralDetailsPropsType> = observer(
    'GeneralDetails',
    (props: GeneralDetailsPropsType): ReactElement => {
        const { routerBackoffice } = useAppStateContext();
        const common = useCommon();
        const { generalDetailsState, editionDisabled } = CampaignCreatorState.get(common, props.campaignId);
        return (
            <div
                className='grid grid-cols-2 gap-2'
                data-test={GENERAL_DETAILS_ROOT}
            >
                <div className='flex flex-col gap-8'>
                    <section
                        data-test='basic-info'
                        className='flex flex-col gap-6'
                    >
                        <h2 className='m-0 text-[1rem] font-semibold leading-[1.75]'>Basic Information</h2>
                        <div className='flex flex-col gap-4'>
                            <Select
                                dataTest='basic-info-dropdown'
                                label='Campaign Type'
                                placeholder='Select'
                                formInputState={generalDetailsState.campaignTypeForm}
                                values={[
                                    {
                                        dataTest: 'bet-and-get',
                                        value: 'bet-and-get',
                                        label: 'Bet and Get',
                                    },
                                    {
                                        dataTest: 'settlement',
                                        value: 'settlement',
                                        label: 'Settlement',
                                    },
                                    {
                                        dataTest: 'promo-code',
                                        value: 'promo-code',
                                        label: 'Promo Code',
                                    },
                                ]}
                                errors={generalDetailsState.campaignTypeForm.errorsForView}
                                disabled={editionDisabled}
                            />
                            <Input
                                label='Campaign Name'
                                placeholder='Type'
                                formInputState={generalDetailsState.nameForm}
                                errors={generalDetailsState.nameForm.errorsForView}
                                disabled={editionDisabled}
                            />
                            <div className='flex flex-col gap-2'>
                                <label className='text-sm'>Terms & Conditions</label>
                                <Textarea
                                    formState={generalDetailsState.termsAndConditionsForm}
                                    ckeditor
                                    isReadOnly={editionDisabled}
                                    customErrorMessage={
                                        generalDetailsState.termsAndConditionsForm.errorsForView.length > 0 ? (
                                            <span className='text-sm text-destructive mt-2'>
                                                {generalDetailsState.termsAndConditionsForm.errorsForView[0]}
                                            </span>
                                        ) : undefined
                                    }
                                />
                            </div>
                            <CheckboxWithState
                                label='Require User Opt-In'
                                formInputState={generalDetailsState.optInOnlyForm}
                                errors={generalDetailsState.optInOnlyForm.errorsForView}
                                disabled={editionDisabled}
                            />
                        </div>
                    </section>

                    <section
                        data-test='campaign-period'
                        className='flex flex-col gap-6'
                    >
                        <h2 className='m-0 text-[1rem] font-semibold leading-[1.75]'>Campaign Time Period</h2>
                        <div className='flex flex-col gap-4'>
                            <Select<StartAtTypeType>
                                data-test='frequency-period-dropdown'
                                label='Start'
                                placeholder='Select'
                                formInputState={generalDetailsState.startTypeForm}
                                values={[
                                    {
                                        dataTest: 'custom',
                                        value: 'custom',
                                        label: 'Custom',
                                    },
                                    {
                                        dataTest: 'once-campaign-activated',
                                        value: 'once-campaign-activated',
                                        label: 'Once Campaign Activated',
                                    },
                                ]}
                                errors={generalDetailsState.startTypeForm.errorsForView}
                                disabled={editionDisabled}
                            />
                            <div className='grid grid-cols-2 gap-4'>
                                <Calendar
                                    dataTest='start-date'
                                    label='Start Date & Time (UK time)'
                                    placeholder='*Current Date*'
                                    formInputState={generalDetailsState.startAtForm}
                                    firstDayOfWeek='monday'
                                    showTimePicker={true}
                                    timezone={UK_TIMEZONE}
                                    errors={generalDetailsState.startAtForm.errorsForView}
                                    disabled={generalDetailsState.startDateDisabled || editionDisabled}
                                />
                                <Calendar
                                    dataTest='end-date'
                                    label='End Date & Time (UK time)'
                                    placeholder='*Current Date*'
                                    formInputState={generalDetailsState.expiryAtForm}
                                    firstDayOfWeek='monday'
                                    showTimePicker={true}
                                    timezone={UK_TIMEZONE}
                                    errors={generalDetailsState.expiryAtForm.errorsForView}
                                    disabled={
                                        generalDetailsState.endDateDisabled &&
                                        routerBackoffice.bonusCampaignParams?.type !== 'NEW'
                                    }
                                />
                            </div>
                        </div>
                    </section>
                    <section
                        data-test='time-limit'
                        className='flex flex-col gap-6'
                    >
                        <h2 className='m-0 text-[1rem] font-semibold leading-[1.75]'>Time Limit</h2>
                        <div className='grid grid-cols-2 gap-4'>
                            <Select
                                data-test='time-limit-trigger-dropdown'
                                label='Trigger (Optional)'
                                placeholder='Select'
                                formInputState={generalDetailsState.timeLimitTriggerForm}
                                values={[
                                    {
                                        dataTest: 'opt-in',
                                        value: 'opt-in',
                                        label: 'Opt In',
                                    },
                                    {
                                        dataTest: 'account-creation',
                                        value: 'account-creation',
                                        label: 'Account Creation',
                                    },
                                ]}
                                errors={generalDetailsState.timeLimitTriggerForm.errorsForView}
                                disabled={editionDisabled}
                                canBeCleared
                            />
                            <Input
                                label='Duration (hours)'
                                placeholder='Type'
                                formInputState={generalDetailsState.timeLimitForm}
                                errors={generalDetailsState.timeLimitForm.errorsForView}
                                disabled={editionDisabled || generalDetailsState.timeLimitTriggerForm.value === ''}
                            />
                        </div>
                    </section>

                    <section
                        data-test='campaign-frequency'
                        className='flex flex-col gap-6'
                    >
                        <h2 className='m-0 text-[1rem] font-semibold leading-[1.75]'>Frequency</h2>
                        <div className='grid grid-cols-2 gap-4'>
                            <Select
                                label='Usage Frequency'
                                placeholder='Select'
                                formInputState={generalDetailsState.frequencyPeriodForm}
                                values={[
                                    {
                                        value: 'one-time',
                                        label: 'Only Once',
                                    },
                                    {
                                        value: 'daily',
                                        label: 'Daily',
                                    },
                                    {
                                        value: 'weekly',
                                        label: 'Weekly',
                                    },
                                    {
                                        value: 'monthly',
                                        label: 'Monthly',
                                    },
                                    {
                                        value: 'time-bound',
                                        label: 'Time Bound',
                                    },
                                ]}
                                errors={generalDetailsState.frequencyPeriodForm.errorsForView}
                                disabled={editionDisabled}
                            />
                            <Input
                                label='Number of Uses in Frequency'
                                placeholder='Type'
                                formInputState={generalDetailsState.numberOfUsesForm}
                                errors={generalDetailsState.numberOfUsesForm.errorsForView}
                                description='Enter a number between 1 and 100.'
                                disabled={editionDisabled || generalDetailsState.frequencyPeriod === 'one-time'}
                            />
                        </div>
                    </section>
                </div>
            </div>
        );
    }
);
